"use client";

import React, { useEffect, useState } from "react";
import { Menu, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-6 items-center">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-md bg-muted/20" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex" viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 w-max items-center justify-center bg-transparent py-1 text-xs font-default uppercase tracking-[0.2em] text-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none hover:bg-transparent focus:bg-transparent">
                <span className="relative z-10">{item.label}</span>
                <span className="absolute bottom-0 left-0 right-0 h-[1px] origin-right scale-x-0 bg-foreground transition-transform duration-300 ease-out group-hover:origin-left group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [open, setOpen] = useState(false);
  if (loading) {
    return <div className="space-y-4 pt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-muted/20" />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-6 pt-10">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-serif tracking-wide text-foreground hover:text-primary transition-colors border-b border-border/10 pb-4 flex items-center justify-between group" onClick={() => setOpen(false)}>
          <span>{item.label}</span>
          <ArrowUpRight className="size-5 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>)}
      <div className="pt-6 space-y-4">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Direct Contact</div>
        <Link to="tel:5129560639" className="block text-lg font-default tracking-wider hover:text-primary transition-colors">
          512-956-0639
        </Link>
        <Link to="mailto:gfcllcs@gmail.com" className="block text-sm font-default text-muted-foreground hover:text-primary transition-colors">
          gfcllcs@gmail.com
        </Link>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264911" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border/20 shadow-sm py-3" : "bg-background/80 backdrop-blur-sm border-b border-border/10 py-5"}`}>
      <MenuProvider menu_id="16" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <WvcLogo className="h-7 w-auto" />
        </Link>
        <DesktopMenuConsumer />
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="hidden md:inline-flex uppercase text-xs tracking-[0.2em] px-5 py-2 border-foreground/20 hover:bg-foreground hover:text-background transition-colors cursor-pointer">
                Request Estimate
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-card border-border min-w-[200px] rounded-md">
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-accent focus:text-accent-foreground py-3 px-4 font-mono uppercase tracking-wider text-sm">
                <Link to="tel:512-956-0639">Call Us</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-accent focus:text-accent-foreground py-3 px-4 font-mono uppercase tracking-wider text-sm">
                <Link to="mailto:gfcllcs@gmail.com">Email Us</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden size-10 hover:bg-transparent">
                <Menu className="size-6" />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-6 overflow-y-auto">
              <MenuProvider menu_id="16">
                <MobileMenuContent />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </MenuProvider>
    </section>;
}